import type { GuidedAnswers, GuidedUnsureFlags } from "@/features/blueprints/blueprintTypes";
import {
  GUIDED_TEXT_MAX_LENGTH,
  type GuidedQuestion,
} from "@/features/blueprints/guided/guidedTypes";

type GuidedQuestionStepProps = {
  question: GuidedQuestion;
  questionNumber: number;
  totalQuestions: number;
  answers: GuidedAnswers;
  unsureFlags: GuidedUnsureFlags;
  onAnswersChange: (answers: GuidedAnswers) => void;
  onUnsureFlagsChange: (flags: GuidedUnsureFlags) => void;
  onBack: () => void;
  onNext: () => void;
  canGoNext: boolean;
  saveLabel: string;
};

function getTextValue(question: GuidedQuestion, answers: GuidedAnswers): string {
  if (question.id === "q1") {
    return answers.q1?.appIdea ?? "";
  }

  if (question.id === "q2") {
    return answers.q2?.text ?? "";
  }

  if (question.id === "q3") {
    return answers.q3?.extraText ?? "";
  }

  if (question.id === "q5") {
    return answers.q5?.extraText ?? "";
  }

  return "";
}

function getSelectedChips(question: GuidedQuestion, answers: GuidedAnswers): string[] {
  if (question.id === "q2") {
    return answers.q2?.chips ?? [];
  }

  if (question.id === "q3") {
    return answers.q3?.chips ?? [];
  }

  if (question.id === "q4") {
    return answers.q4?.chips ?? [];
  }

  if (question.id === "q5") {
    return answers.q5?.chips ?? [];
  }

  return [];
}

function updateTextAnswer(
  question: GuidedQuestion,
  answers: GuidedAnswers,
  value: string,
): GuidedAnswers {
  if (question.id === "q1") {
    return { ...answers, q1: { ...answers.q1, appIdea: value } };
  }

  if (question.id === "q2") {
    return { ...answers, q2: { ...answers.q2, text: value } };
  }

  if (question.id === "q3") {
    return { ...answers, q3: { ...answers.q3, extraText: value } };
  }

  if (question.id === "q5") {
    return { ...answers, q5: { ...answers.q5, extraText: value } };
  }

  return answers;
}

function updateChipsAnswer(
  question: GuidedQuestion,
  answers: GuidedAnswers,
  chips: string[],
): GuidedAnswers {
  if (question.id === "q2") {
    return { ...answers, q2: { ...answers.q2, chips } };
  }

  if (question.id === "q3") {
    return { ...answers, q3: { ...answers.q3, chips } };
  }

  if (question.id === "q4") {
    return { ...answers, q4: { ...answers.q4, chips } };
  }

  if (question.id === "q5") {
    return { ...answers, q5: { ...answers.q5, chips } };
  }

  return answers;
}

function clearQuestionAnswer(
  question: GuidedQuestion,
  answers: GuidedAnswers,
): GuidedAnswers {
  if (question.id === "q2") {
    return { ...answers, q2: { text: "", chips: [] } };
  }

  if (question.id === "q3") {
    return { ...answers, q3: { chips: [], extraText: "" } };
  }

  if (question.id === "q4") {
    return { ...answers, q4: { chips: [] } };
  }

  if (question.id === "q5") {
    return { ...answers, q5: { chips: [], extraText: "" } };
  }

  return answers;
}

export function GuidedQuestionStep({
  question,
  questionNumber,
  totalQuestions,
  answers,
  unsureFlags,
  onAnswersChange,
  onUnsureFlagsChange,
  onBack,
  onNext,
  canGoNext,
  saveLabel,
}: GuidedQuestionStepProps) {
  const textValue = getTextValue(question, answers);
  const selectedChips = getSelectedChips(question, answers);
  const isUnsure = Boolean(unsureFlags[question.id]);

  function handleTextChange(value: string) {
    onAnswersChange(updateTextAnswer(question, answers, value));

    if (value.trim()) {
      onUnsureFlagsChange({ ...unsureFlags, [question.id]: false });
    }
  }

  function handleChipToggle(chip: string) {
    const nextChips = selectedChips.includes(chip)
      ? selectedChips.filter((item) => item !== chip)
      : [...selectedChips, chip];

    onAnswersChange(updateChipsAnswer(question, answers, nextChips));

    if (nextChips.length > 0) {
      onUnsureFlagsChange({ ...unsureFlags, [question.id]: false });
    }
  }

  function handleUnsureToggle() {
    const nextValue = !isUnsure;
    onUnsureFlagsChange({ ...unsureFlags, [question.id]: nextValue });

    if (nextValue) {
      onAnswersChange(clearQuestionAnswer(question, answers));
    }
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-indigo-700">
            Question {questionNumber} of {totalQuestions}
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
            {question.title}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            {question.helper}
          </p>
        </div>
        <span className="rounded-md bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
          {saveLabel}
        </span>
      </div>

      {question.id === "q1" ? (
        <div className="mt-6">
          <label
            htmlFor="guided-project-name"
            className="block text-sm font-medium text-slate-700"
          >
            Project name optional
          </label>
          <input
            id="guided-project-name"
            value={answers.q1?.projectName ?? ""}
            onChange={(event) =>
              onAnswersChange({
                ...answers,
                q1: { ...answers.q1, projectName: event.target.value },
              })
            }
            maxLength={160}
            className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
        </div>
      ) : null}

      {question.inputType === "text" || question.optionalTextLabel ? (
        <div className="mt-6">
          <label
            htmlFor={`guided-${question.id}-text`}
            className="block text-sm font-medium text-slate-700"
          >
            {question.optionalTextLabel ?? "Your answer"}
          </label>
          <textarea
            id={`guided-${question.id}-text`}
            value={textValue}
            onChange={(event) => handleTextChange(event.target.value)}
            maxLength={GUIDED_TEXT_MAX_LENGTH}
            rows={question.id === "q1" ? 7 : 4}
            disabled={isUnsure}
            className="mt-2 w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100 disabled:text-slate-500"
          />
          <p className="mt-2 text-xs text-slate-500">
            {textValue.length} / {GUIDED_TEXT_MAX_LENGTH}
          </p>
        </div>
      ) : null}

      {question.chips ? (
        <div className="mt-6 flex flex-wrap gap-2">
          {question.chips.map((chip) => {
            const isSelected = selectedChips.includes(chip);

            return (
              <button
                key={chip}
                type="button"
                onClick={() => handleChipToggle(chip)}
                disabled={isUnsure}
                className={`rounded-md border px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  isSelected
                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                {chip}
              </button>
            );
          })}
        </div>
      ) : null}

      {question.allowUnsure ? (
        <button
          type="button"
          onClick={handleUnsureToggle}
          className={`mt-5 rounded-md border px-3 py-2 text-sm font-semibold transition ${
            isUnsure
              ? "border-amber-300 bg-amber-50 text-amber-800"
              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
          }`}
        >
          I don't know
        </button>
      ) : null}

      <div className="mt-6 flex flex-wrap justify-between gap-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canGoNext}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
        >
          Next
        </button>
      </div>
    </section>
  );
}
